import type { ChangeEvent, KeyboardEvent } from "react";

import { Fido2Attestation, RecoveryKeyAttestation } from "@dfns/sdk";

import { base64url } from "./base64url";

// Define RecoverUserInput locally
export type RecoverUserInput = {
  kind: "RecoveryKey";
  credentialAssertion: {
    credId: string;
    clientData: string;
    signature: string;
  };
};

export type KeyClientData = {
  type: "key.create";
  challenge: string;
  origin: string;
  crossOrigin: boolean;
};

export type NewUserCredentials = {
  recoveryFactor: {
    kind: "RecoveryKey";
    credentialId: string;
    signature: {
      clientData: ArrayBuffer;
      attestationData: ArrayBuffer;
    };
    encryptedPrivateKey?: string;
  };
};

// GM-2426: a recovery kit Secret is capital letters and digits in 7 hyphen-separated groups of these
// lengths (34 characters in all). The pattern, the formatter and the placeholder are all built from
// this one list, so they can't drift apart.
const SECRET_GROUP_LENGTHS = [2, 6, 5, 5, 5, 5, 6];
const SECRET_LENGTH = SECRET_GROUP_LENGTHS.reduce((sum, n) => sum + n, 0);
export const RECOVERY_SECRET_PATTERN = new RegExp(`^${SECRET_GROUP_LENGTHS.map((n) => `[A-Z0-9]{${n}}`).join("-")}$`);
export const RECOVERY_SECRET_PLACEHOLDER = SECRET_GROUP_LENGTHS.map((n) => "X".repeat(n)).join("-");
const SECRET_GROUPS = new RegExp(`^${SECRET_GROUP_LENGTHS.map((n) => `(.{0,${n}})`).join("")}`);

// Used for typing and paste alike: uppercases, drops everything except A-Z and 0-9 (spaces, line
// breaks, any kind of dash, stray symbols), keeps the first SECRET_LENGTH characters and re-inserts
// the hyphens.
export const formatSecret = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, SECRET_LENGTH)
    .match(SECRET_GROUPS)!
    .slice(1)
    .filter(Boolean)
    .join("-");

const countSecretChars = (text: string) => text.replace(/[^A-Za-z0-9]/g, "").length;

// formatSecret rewrites the whole value, which moves the caret to the end. When the user is editing
// mid-value, write the formatted value into the input now and put the caret after the same number of
// secret characters it followed. React then finds the value unchanged and leaves the caret alone; doing
// it later (e.g. in requestAnimationFrame) would let a fast next keystroke land at the end.
// antd hands onChange a detached clone of the input as e.target, so use the real one on nativeEvent.
export const keepSecretCaret = ({ nativeEvent }: ChangeEvent<HTMLInputElement>) => {
  const input = nativeEvent.target as HTMLInputElement;
  const caret = input.selectionStart ?? input.value.length;
  if (caret === input.value.length) return;
  const charsBefore = countSecretChars(input.value.slice(0, caret));
  input.value = formatSecret(input.value);
  let pos = 0;
  while (pos < input.value.length && countSecretChars(input.value.slice(0, pos)) < charsBefore) pos++;
  input.setSelectionRange(pos, pos);
};

// Backspace right after a hyphen (or Delete right before one) would only remove the hyphen, which
// formatSecret puts straight back. Step the caret over the hyphen first so the key removes the
// neighbouring character instead.
export const skipSecretHyphen = (e: KeyboardEvent<HTMLInputElement>) => {
  const { selectionStart: start, selectionEnd: end, value } = e.currentTarget;
  if (start === null || start !== end) return;
  if (e.key === "Backspace" && value[start - 1] === "-") e.currentTarget.setSelectionRange(start - 1, start - 1);
  if (e.key === "Delete" && value[start] === "-") e.currentTarget.setSelectionRange(start + 1, start + 1);
};

// GM-2435: maps a recovery failure to a message the admin can act on, as the Customer Portal does.
export const passkeyErrorMessage = (errorName: string | undefined, error: string | undefined, fallback: string): string => {
  if (errorName === "NotAllowedError")
    return "The passkey request was cancelled or timed out. Please try again and confirm with your PIN, fingerprint or face.";
  if (errorName === "InvalidStateError")
    return "This browser or device already has a passkey saved for this account, so a new one can't be created here. Use your existing passkey or try a different browser or device.";
  // The worker can't decrypt the stored recovery key with this Secret (the browser's crypto API names it OperationError).
  if (errorName === "OperationError") return "The Secret doesn't match this recovery kit. Check that it's copied exactly.";
  // Dfns archives a recovery kit once it has been used to recover.
  if (error?.includes("Recovery key is archived")) return "This recovery kit has already been used. Use the new kit from your last recovery.";
  if (error?.includes("Recovery key not found")) return "Recovery kit not found. Check that the Credential ID and Secret are copied exactly.";
  return fallback;
};

type NewCredentialRegistration = {
  firstFactorCredential: Fido2Attestation;
  recoveryCredential: RecoveryKeyAttestation;
};

export const createRecoveryCredential = async (
  clientData: KeyClientData,
  username: string
): Promise<{
  credential: RecoveryKeyAttestation;
  recoveryKey: { secret: string; credentialId: string };
}> => {
  return new Promise((resolve, reject) => {
    const serviceWorker = new Worker("/js/worker.js");

    serviceWorker.addEventListener("message", (event) => {
      switch (event.data.type) {
        case "encryptedPrivateKeyAndPublicKey": {
          const { encryptedPrivateKey, attestationData, recoveryKey, credentialId } = event.data;
          const recoveryKeyCredential: RecoveryKeyAttestation = {
            credentialKind: "RecoveryKey",
            credentialInfo: {
              credId: credentialId,
              attestationData: base64url(attestationData),
              clientData: base64url(JSON.stringify(clientData)),
            },
            encryptedPrivateKey: encryptedPrivateKey,
          };
          const newRecoveryKey = {
            secret: recoveryKey,
            credentialId,
          };
          resolve({
            credential: recoveryKeyCredential,
            recoveryKey: newRecoveryKey,
          });
          break;
        }
        case "error": {
          const { error } = event.data;
          reject(error);
          break;
        }
      }
    });

    serviceWorker.postMessage({
      type: "generateEncryptedPrivateKeyAndPublicKey",
      username: username,
      clientData: JSON.stringify(clientData),
    });
  });
};

export const validateRecoveryKey = async (
  username: string,
  existingRecoveryKey: {
    recoveryKey: string;
    credentialId: string;
    encryptedKey: string;
  }
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const serviceWorker = new Worker("/js/worker.js");

    serviceWorker.addEventListener("message", (event) => {
      switch (event.data.type) {
        case "recoveryKeyIsValid": {
          resolve();
          break;
        }
        case "error": {
          const { error } = event.data;
          reject(error);
          break;
        }
      }
    });

    serviceWorker.postMessage({
      type: "validateRecoveryKey",
      username: username,
      recoveryKey: existingRecoveryKey.recoveryKey,
      encryptedPrivateKey: existingRecoveryKey.encryptedKey,
      credentialId: existingRecoveryKey.credentialId,
    });
  });
};

export const signRecoveryCredentials = async (
  username: string,
  existingRecoveryKey: {
    recoveryKey: string;
    credentialId: string;
    encryptedKey: string;
  },
  newCredentials: NewCredentialRegistration
): Promise<RecoverUserInput> => {
  return new Promise((resolve, reject) => {
    const recoveryClientData = JSON.stringify({
      type: "key.get",
      challenge: base64url(JSON.stringify(newCredentials)),
      origin: window.location.origin,
      crossOrigin: false,
    });

    const serviceWorker = new Worker("/js/worker.js");

    serviceWorker.addEventListener("message", (event) => {
      switch (event.data.type) {
        case "signature": {
          const { signature } = event.data;

          resolve({
            kind: "RecoveryKey",
            credentialAssertion: {
              credId: existingRecoveryKey.credentialId,
              clientData: base64url(recoveryClientData),
              signature: signature,
            },
          } as RecoverUserInput);
          break;
        }
        case "error": {
          const { error } = event.data;
          reject(error);
          break;
        }
      }
    });

    serviceWorker.postMessage({
      type: "generateSignature",
      username: username,
      message: recoveryClientData,
      recoveryKey: existingRecoveryKey.recoveryKey,
      encryptedPrivateKey: existingRecoveryKey.encryptedKey,
      credentialId: existingRecoveryKey.credentialId,
    });
  });
};
